import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * TickProvider global.
 *
 * Cada componente que precisa re-renderizar a cada N ms (ex.: cronômetro de
 * "Em curso" / urgência de ticket) costumava montar seu próprio
 * `setInterval(..., 15000)`. Em uma jornada com 7 tickets "Started", isso
 * vira 7 timers paralelos competindo pelo mesmo tick, todos forçando re-render
 * na mesma subárvore — overhead de scheduler + pressão de memória.
 *
 * A solução: um único timer no topo da app. Componentes que precisam do tick
 * chamam `useTick()`, que retorna `Date.now()` atualizado a cada `interval`
 * ms. Como `now` muda, o componente re-renderiza e qualquer cálculo de
 * "minutos decorridos" que leia `Date.now()` internamente se mantém fresco.
 *
 * O hook tem fallback: se usado fora do `<TickProvider>`, cria um interval
 * local próprio (mantém testes isolados funcionando sem precisar prover o
 * contexto).
 */
interface TickContextValue {
  /** ms epoch atual, atualizado a cada `interval` ms. */
  now: number
  /** ms entre ticks (default 15000). */
  interval: number
}
const TickCtx = createContext<TickContextValue | null>(null)

export function TickProvider({ children, interval = 15000 }: { children: ReactNode; interval?: number }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const iv = setInterval(() => {
      // R-9: aba oculta não precisa re-render dos cronômetros — ticks já são
      // throttlados pelo browser, guard evita trabalho desperdiçado.
      if (typeof document !== 'undefined' && document.hidden) {return}
      setNow(Date.now())
    }, interval)
    return () => clearInterval(iv)
  }, [interval])
  return <TickCtx.Provider value={{ now, interval }}>{children}</TickCtx.Provider>
}

export function useTick(intervalMs = 15000): number {
  const ctx = useContext(TickCtx)
  // Fallback local: cada componente fora do provider monta seu próprio timer.
  // Isso preserva testes isolados e previne render "stale" se o provider for
  // esquecido em alguma subárvore.
  const [localNow, setLocalNow] = useState(() => Date.now())
  useEffect(() => {
    if (ctx) {return}
    const iv = setInterval(() => setLocalNow(Date.now()), intervalMs)
    return () => clearInterval(iv)
  }, [ctx, intervalMs])
  return ctx ? ctx.now : localNow
}